import type { QuestionDetail } from "@models/Dataset";
import type { DatasetMetric, MetricBase } from "@models/Metric";
import type { ScorecardSourceQuestionKind } from "@models/Scorecard";
import type { WidgetRollup } from "@models/Rollup";

// Re-exported: the metric -> represented-results adapter, the question-detail
// fetch predicate, the search/sort helper, and the Base bridge are all
// identical for every DatasetMetric consumer (Scorecard rows, Bar Chart
// bars, ...) — see dataset-metric-representation-utils.ts. Scorecard-specific
// code below (brand-mapping eligibility, fetch-field snapshotting) stays here.
export {
  getMetricResultCandidates,
  metricNeedsQuestionDetailFetch,
  filterAndSortDatasetMetrics,
  metricBaseToVisualizationBase,
} from "@utils/dataset-metric-representation-utils";

/**
 * Whether a saved Dataset Metric's results can be mapped individually to
 * Scorecard brands, per the eligibility table: every source kind is eligible
 * once its definition is resolved, except a one-dimensional Single metric
 * focused on a Rollup — a rollup of that kind collapses multiple brand-
 * shaped response values into a single result, so there's nothing left to
 * map per brand. Every other rollup (Single grid Value Focus) rolls up
 * columns, not the brand-bearing rows/variables, so it stays eligible.
 */
export function isMetricEligibleForBrandMapping(
  metric: DatasetMetric,
): { eligible: boolean; reason?: string } {
  if (!metric.source) {
    return { eligible: false, reason: "This metric's definition isn't resolved yet." };
  }
  if (metric.source.kind === "single_one_dimensional" && metric.source.focus.mode === "rollup") {
    return {
      eligible: false,
      reason:
        "This metric's rollup combines multiple response values into one result, so it can't be mapped per brand.",
    };
  }
  return { eligible: true };
}

export interface DatasetMetricRowFetchFields {
  sourceQuestionId: string;
  sourceQuestionKind: ScorecardSourceQuestionKind;
  focusMode: "VALUE" | "VARIABLE" | null;
  metricValueCode: string | null;
  rollup: WidgetRollup | null;
  useTotalBase: boolean;
}

/**
 * Resolves the fetch-relevant fields a Metric row caches at save time (see
 * ScorecardDatasetMetricRow) from the metric's own definition — mirrors
 * Question rows' getFocusParamsForRow (scorecard-fetch-utils.ts), but as a
 * one-time save-time snapshot rather than a per-render derivation, since the
 * live DatasetMetric isn't re-fetched at render time.
 *
 * Value/rollup codes come from questionDetail.values' own `.value` field
 * (not `.id`, not `.title`) — same convention Question rows already use
 * (see useGridMetricSelection.ts's buildSaveFields). Variable-focus codes
 * come from the variable's own `.label` field — the same convention Bar/
 * Donut/Line's "Group By" VARIABLE focus mode already uses (see
 * FocusModeSection.tsx), not `.id` or `.title`.
 *
 * useTotalBase mirrors the metric's own Metric population choice — true
 * whenever population.mode is "total_survey_population", false for
 * "valid_question_respondents" (always false for Mean metrics, since that
 * mode is fixed for them). This is the one request-level effect Metric
 * population has on the live fetch; every other population distinction
 * remains out of scope.
 */
export function buildDatasetMetricRowFetchFields(
  metric: DatasetMetric,
  questionDetail: QuestionDetail | null,
): DatasetMetricRowFetchFields {
  const source = metric.source;
  const sourceQuestionId = source?.question.questionId ?? "";
  const useTotalBase = metric.population.mode === "total_survey_population";
  if (!source) {
    return {
      sourceQuestionId,
      sourceQuestionKind: "one_dimensional",
      focusMode: null,
      metricValueCode: null,
      rollup: null,
      useTotalBase,
    };
  }

  switch (source.kind) {
    case "single_one_dimensional":
    case "multiple_one_dimensional":
    case "numeric":
      return {
        sourceQuestionId,
        sourceQuestionKind: "one_dimensional",
        focusMode: null,
        metricValueCode: null,
        rollup: null,
        useTotalBase,
      };

    case "single_grid_value_focus": {
      const focus = source.focus;
      if (focus.mode === "one") {
        const code = questionDetail?.values.find((v) => v.id === focus.valueId)?.value;
        return {
          sourceQuestionId,
          sourceQuestionKind: "single_grid",
          focusMode: "VALUE",
          metricValueCode: code !== undefined ? String(code) : null,
          rollup: null,
          useTotalBase,
        };
      }
      // mode === "rollup"
      const rollup = focus.rollup;
      const sourceCategoryLabels = rollup.sourceIds
        .map((id) => questionDetail?.values.find((v) => v.id === id)?.value)
        .filter((code): code is number => code !== undefined)
        .map((code) => String(code));
      return {
        sourceQuestionId,
        sourceQuestionKind: "single_grid",
        focusMode: "VALUE",
        metricValueCode: null,
        rollup: {
          id: rollup.id,
          name: rollup.name,
          calculationType: rollup.calculationType,
          sourceCategoryLabels,
        },
        useTotalBase,
      };
    }

    case "single_grid_variable_focus": {
      const focus = source.focus;
      const code = questionDetail?.rowVariables.find((v) => v.id === focus.variableId)?.label;
      return {
        sourceQuestionId,
        sourceQuestionKind: "single_grid",
        focusMode: "VARIABLE",
        metricValueCode: code ?? null,
        rollup: null,
        useTotalBase,
      };
    }
  }
}
